import firebase from 'firebase/app';
import { db } from 'contexts/FirebaseContext';
import { deCryptMessage } from '../security';

type User = {
  _id?: string | number;
  id?: string | number;
  name?: string;
  avatar?: string;
  fullname?: string;
  chatId?: string;
};

export type ChatItem = {
  chatId: string;
  userId: string;
  therapistId: string;
  therapistUId: string;
  e2ee: boolean;
  emotion?: number;
  user: User;
};

export type Message = {
  _id: string | number;
  text: string;
  system?: boolean;
  textEncrypted?: string;
  createdAt: Date | number;
  user: User;
  image?: string;
  chatId?: string;
  sent: boolean;
  senderId: string;
  received: boolean;
  parentMessage?: Message;
};

export function getUsersByIds(ids: string[]) {
  return new Promise((res) => {
    // don't run if there aren't any ids or a path for the collection
    if (!ids || !ids.length) return res([]);

    const collectionPath = db.collection('users');
    let batches = [];

    while (ids.length) {
      // firestore limits batches to 10
      const batch = ids.splice(0, 10);

      // add the batch request to to a queue
      batches.push(
        new Promise((response) => {
          collectionPath
            .where(firebase.firestore.FieldPath.documentId(), 'in', [...batch])
            .get()
            .then((results) =>
              response(results.docs.map((result) => ({ ...result.data(), id: result.id })))
            );
        })
      );
    }

    // after all of the data is fetched, return it
    Promise.all(batches).then((content) => {
      res(content.flat());
    });
  });
}

export function getMyChats(therapistId: string, onSnapshot: (chats: any) => void) {
  return db
    .collection('chats')
    .where('therapistId', '==', therapistId)
    .onSnapshot(async (snapshot) => {
      const chats: any = snapshot.docs.map((doc) => {
        return {
          chatId: doc.id,
          ...doc.data()
        };
      });

      const userIds = chats.map((chat: any) => {
        return chat.userId;
      });

      if (userIds.length) {
        await getUsersByIds(userIds).then((usersData: any) => {
          const formattedChats: ChatItem[] = chats.map((chat: ChatItem) => {
            const index = usersData.findIndex((user: any) => user.id === chat?.userId);
            return { ...chat, user: usersData?.[index] ?? {} };
          });
          onSnapshot(formattedChats);
        });
      }
    });
}

export function getMessages(
  chatId: string,
  limit: number = 100,
  order: firebase.firestore.OrderByDirection = 'asc',
  eThree: any,
  groupChat: any,
  onSnapshot: (messages: Message[]) => void
) {
  const unlisten = db
    .collection(`messages/${chatId}/conversation`)
    .orderBy('createdAt', order)
    .limit(limit)
    .onSnapshot(async (snapshot) => {
      const docs = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const message = doc.data();
          let decryptedMessage = '';
          let decryptedParentMessage = '';
          if (message?.e2ee && groupChat) {
            try {
              const messageSender = await eThree?.findUsers(message.senderUId);
              decryptedMessage = await groupChat.decrypt(message?.text, messageSender);

              if (message?.parentMessage) {
                const parentMessageSender = await eThree?.findUsers(
                  message?.parentMessage?.senderUId
                );
                decryptedParentMessage = await groupChat?.decrypt(
                  message?.parentMessage?.text,
                  parentMessageSender
                );
              }
            } catch (decryptError) {
              console.log({ decryptError });
            }
          } else {
            decryptedMessage = deCryptMessage(message.text);
            decryptedParentMessage = message?.parentMessage
              ? deCryptMessage(message?.parentMessage?.text)
              : '';
          }
          return {
            docId: doc.id,
            ...message,
            text: decryptedMessage,
            textEncrypted: message.text,
            sent: true,
            createdAt: message?.createdAt?.toDate?.(),
            ...(message?.parentMessage && {
              parentMessage: {
                ...message?.parentMessage,
                text: decryptedParentMessage
              }
            })
          };
        })
      );
      onSnapshot(docs?.reverse());
    });

  return unlisten;
}

export function getUnreadChatCount(
  chatId: string,
  therapistId: string,
  onSnapshot: (count: number) => void
) {
  const unlisten = db
    .collection(`messages/${chatId}/conversation`)
    .where('received', '==', false)
    .where('senderId', '!=', therapistId)
    .onSnapshot((snapshot) => {
      const messages = snapshot.docs.map((doc) => doc.data());
      const filteredMessages = messages.filter(({ system }) => !system);
      return onSnapshot(filteredMessages.length);
    });

  return unlisten;
}

export function sendMessage(message: Message, chatId: string) {
  return db.collection(`messages/${chatId}/conversation`).doc().set(message);
}

export function updateMessageStatus(messages: any = [], chatId: string) {
  const batch = db.batch();
  messages.forEach((message: any) => {
    const docRef = db.collection(`messages/${chatId}/conversation`).doc(message.docId);
    batch.update(docRef, { received: true });
  });

  batch.commit();
}

export function setTyping(typing: boolean, chatId: string | undefined, userId: string | undefined) {
  if (chatId && userId) {
    db.collection('messages')
      .doc(chatId)
      .set({ typing: { [userId]: typing } }, { merge: true });
  }
}

export function getChatTyping(chatId: string, onSnapshot: (data: any) => void) {
  const unListen = db
    .collection('messages')
    .doc(chatId)
    .onSnapshot((snapshot) => {
      onSnapshot(snapshot.data());
    });
  return unListen;
}
